const asyncImport = require('util/asyncImport').default;
const { MAIN_EASING, DELAY_DEBOUNCE } = require('./consts');

const { getNumber, injectFocusablesList, trackGtm4 } = require('./functions');
let { mediumDelay } = require('./sass-variables').default;
const isRtl = require('./isRtl').default;

mediumDelay = getNumber(mediumDelay);

const CLASS_ACTIVE = 'active';
const CLASS_CHANGING_PAGE = 'changing-page';

module.exports = {
	async initialize(root, firstPage, pages, trackingWidget, trackingTitle) {
		this.paginationContainer = root.querySelector('.pages-nav');

		if (this.paginationContainer) {
			this.firstPage = root.querySelector(firstPage);
			this.pageContainer = root.querySelector(pages);
			this.pages =
				this.pageContainer && this.pageContainer.querySelectorAll(':scope > *');
			this.numbersContainer = root.querySelector('.pages-nav--numbers');
			this.stateView = root.querySelector('.pages-nav--state');
			this.firstButton = root.querySelector('.pages-nav--first');
			this.lastButton = root.querySelector('.pages-nav--last');
			await asyncImport(this, ['Hammer', 'debounce', 'anime']);
			this.touch = new this.Hammer(this.pageContainer);
			this.pageCount = this.pageContainer.children.length;

			this.currentPage = 0;

			this.pages.forEach(page => injectFocusablesList(page));

			this.renderPagesButtons(0);
			if (this.pageCount >= 5) this.updateNumbers(0);
			this.updateStateView(0);
			this.resize();
			window.addEventListener(
				'resize',
				this.debounce(this.resize.bind(this), DELAY_DEBOUNCE),
			);

			this.firstButton.addEventListener('click', () => {
				this.setPage(0);
			});
			this.lastButton.addEventListener('click', () => {
				this.setPage(this.pageCount - 1);
			});
			this.numbersContainer.addEventListener('click', ({ target }) => {
				// the event is on the whole set of button so if the current page is clicked,
				// since it have a pointer-events set to none it'll trigger
				// the event on pages-nav--numbers
				// hence the check to avoid having an error
				if (!target.classList.contains('pages-nav--numbers'))
					this.setPage(target.innerHTML - 1);

				if (trackingWidget) {
					trackGtm4({
						event: 'GA4_event_c_content_navigation_click',
						widget_name: 'V3-Investor-key-docs',
						widget_title: trackingTitle
							? trackingTitle.innerHTML.trim()
							: 'No title',
						widget_type: 'document',
						link_text: target ? target.innerHTML.trim() : 'No text',
						link_url: 'No url',
						interaction_type: `page ${
							target ? target.innerHTML.trim() : 'No text'
						}`,
					});
				}
			});

			this.touch.on('swipeleft', () => {
				this.increment(isRtl ? -1 : 1);
			});
			this.touch.on('swiperight', () => {
				this.increment(isRtl ? 1 : -1);
			});
			this.updateArias(0);
		}
	},
	renderPagesButtons(currentPage = null) {
		let content = '';
		if (this.pageCount <= 5) {
			for (let i = 0; i < this.pageCount; i++) {
				content += `<button class="pages-nav--number"${
					i === 0 ? '  disabled' : ''
				}>${i + 1}</button>`;
			}
		} else {
			const pagesToShow = 3;
			let maxRange = 2;
			let inStartRange = currentPage - maxRange;
			let inEndRange = currentPage === 0 ? pagesToShow : currentPage + maxRange;
			for (let i = 0; i < this.pageCount; i++) {
				content += `<button class="pages-nav--number${
					(currentPage >= pagesToShow && i === 0) ||
					(currentPage <= this.pageCount - 4 &&
						i === inEndRange &&
						currentPage < this.pageCount - 4)
						? ' dots-after'
						: i !== currentPage &&
						  i !== 0 &&
						  i !== this.pageCount - 1 &&
						  (i < inStartRange || i > inEndRange)
						? ' hidden'
						: ''
				}">${i + 1}</button>`;
			}
		}
		this.numbersContainer.innerHTML = content;
	},
	increment(increment) {
		let target = this.target + increment;
		if (target === this.pageCount) target = 0;
		else if (target < 0) target = this.pageCount - 1;
		this.setPage(target);
	},
	resize() {
		const newWidth = this.pageContainer.offsetWidth;
		if (this.pageWidth !== newWidth) {
			this.target = null; // to force the set page
			this.pageWidth = newWidth;
			this.setPage(this.currentPage);
		}
	},
	updateStateView(page) {
		this.stateView.innerHTML = `${page + 1} / ${this.pageCount}`;
	},
	setPage(page) {
		if (page !== this.target) {
			// activate the overflow hidden during the animation
			this.pageContainer.classList.add(CLASS_CHANGING_PAGE);
			this.target = page;
			const diff = Math.abs(this.currentPage - page);
			const duration = Math.min(mediumDelay * 1000 * diff, 1000);
			if (this.onPageChange) this.onPageChange(page);

			this.anime({
				targets: { page: this.currentPage },
				page,
				duration,
				easing: MAIN_EASING,
				update: ({ animations: [{ currentValue }] }) => {
					const currentPage = Math.round(currentValue);
					this.pageContainer.style.setProperty('--offset', currentValue);
					if (currentPage !== this.currentPage) {
						if (this.pageCount >= 5) this.renderPagesButtons(currentPage);
						this.updateNumbers(currentPage);
						this.updateStateView(currentPage);
						this.updateArias(currentPage);
					}
				},
			}).finished.then(() => {
				// remove the overflow hidden and display only the current page
				this.pageContainer.classList.remove(CLASS_CHANGING_PAGE);
				if (this.pages) {
					this.pages.forEach((page, i) => {
						page.classList.toggle(CLASS_ACTIVE, i === this.target);
					});
				}
			});
		}
	},
	updateNumbers(currentPage) {
		const numbers = this.numbersContainer.children;

		numbers[this.currentPage].disabled = false;
		numbers[currentPage].disabled = true;

		if (currentPage === 0) this.firstButton.disabled = true;
		else this.firstButton.disabled = false;
		if (currentPage === this.pageCount - 1) this.lastButton.disabled = true;
		else this.lastButton.disabled = false;
		this.currentPage = currentPage;
	},
	updateArias(currentPage) {
		this.pages.forEach((page, i) =>
			page.__focusables.forEach(element =>
				element.setAttribute('tabindex', i === currentPage ? 0 : -1),
			),
		);
	},
};
